
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, ArrowRight } from "lucide-react";
import type { BlogPost } from "@shared/schema";
import { updatePageSEO } from "@/lib/seo";

const BlogArchive = () => {
  useEffect(() => {
    updatePageSEO({
      title: "Blog Archive - The Corso Papers",
      description: "Browse through all of Diane Corso's blog posts, insights, and updates.",
      keywords: "Diane Corso blog archive, horror writing archive, blog posts",
      canonical: "https://www.dianecorsofiction.com/blog/archive"
    });
  }, []);

  const { data: posts = [], isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog"],
  });

  if (isLoading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blood-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gothic-white">Loading archive...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20 bg-gothic-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-cinzel font-bold text-blood-red mb-6">Blog Archive</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Browse through all posts from The Corso Papers
            </p>
            <div className="w-24 h-1 bg-blood-red mx-auto mt-6"></div>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-300 mb-4">No blog posts available yet.</p>
              <p className="text-gray-400">Check back soon for updates from Diane's writing journey.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card key={post.id} className="bg-gothic-charcoal border-gothic-gray hover:transform hover:scale-105 transition-all duration-300 overflow-hidden shadow-2xl">
                  {post.featuredImage && (
                    <img 
                      src={post.featuredImage} 
                      alt={`${post.title} featured image`}
                      className="w-full h-48 object-cover" 
                      data-testid={`blog-image-${post.slug}`}
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="300"
                    />
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center text-sm text-gray-400 mb-3">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{new Date(post.publishedAt!).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    <h2 className="text-xl font-playfair font-bold mb-3 text-gothic-white line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-300 mb-4 leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                    <a 
                      href={`/blog/${post.slug}`} 
                      className="inline-flex items-center text-blood-red hover:text-gothic-white font-semibold transition-colors duration-300"
                      data-testid={`read-post-${post.slug}`}
                      aria-label={`Read full post: ${post.title}`}
                    >
                      Read Full Post <ArrowRight className="ml-1" size={16} />
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogArchive;
